import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDarkMode } from '@/hooks/useDarkMode';
import useScrollLock from '@/hooks/useScrollLock';
import { api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Heart, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
export default function WishlistSelectorModal({ isOpen, onClose, productId, productIds, isBulk = false, deckTitle, onSuccess }) {
  const darkMode = useDarkMode();
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Keep the page from scrolling behind this overlay.
  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      fetchWishlists();
    }
  }, [isOpen]);

  const fetchWishlists = async () => {
    try {
      setLoading(true);
      const response = await api.get('/wishlists');
      setWishlists(response.data);
    } catch (error) {
      logger.error('Error fetching wishlists:', error);
      if (!document.querySelector('[data-sonner-toast]')) {
        toast.error('Failed to load wishlists');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToWishlist = async (wishlistId) => {
    try {
      if (isBulk && productIds) {
        // Bulk add all products from deck
        const response = await api.post(`/wishlists/${wishlistId}/products/bulk`, {
          product_ids: productIds
        });
        
        const { added_count, skipped_count } = response.data;
        
        if (added_count > 0) {
          toast.success(`Added ${added_count} product${added_count > 1 ? 's' : ''} from "${deckTitle}" to wishlist!`);
        }
        
        if (skipped_count > 0) {
          toast.info(`${skipped_count} product${skipped_count > 1 ? 's were' : ' was'} already in wishlist`);
        }
        
        if (onSuccess) onSuccess();
      } else {
        // Single product add
        const response = await api.post(`/wishlists/${wishlistId}/products`, null, {
          params: { product_id: productId }
        });
        
        if (response.data.already_exists) {
          toast.info('Product already in this wishlist');
        } else {
          toast.success('Added to wishlist!');
          if (onSuccess) onSuccess();
        }
      }
      onClose();
    } catch (error) {
      logger.error('Error adding to wishlist:', error);
      toast.error('Failed to add to wishlist');
    }
  };

  if (!isOpen) return null;

  // Portal to body so the fixed-position overlay is anchored to the viewport,
  // not to whatever transformed ancestor (e.g. an animated grid section) the
  // triggering card happens to live inside.
  return createPortal(
    <>
      {/* Hide the selector while the create modal is open so its backdrop
          (zIndex 99999) doesn't cover the create modal (z-10000). */}
      {!showCreateModal && (
      <div 
        className="fixed inset-0 flex items-end sm:items-center justify-center"
        style={{
          background: darkMode.isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: 99999,
          pointerEvents: 'auto'
        }}
        onClick={onClose}
      >
        <div 
          className="relative w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden transition-colors max-h-[85vh] sm:max-h-[80vh] flex flex-col"
          style={{ 
            background: darkMode.isDark ? '#2D4354' : 'white',
            pointerEvents: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b transition-colors flex-shrink-0" style={{ borderColor: darkMode.isDark ? '#FED7A5' : '#E5E7EB' }}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-full transition-colors"
              style={{ 
                background: darkMode.isDark ? 'rgba(254, 215, 165, 0.2)' : 'transparent',
                color: darkMode.isDark ? '#FED7A5' : '#73766A'
              }}
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold transition-colors" style={{ color: darkMode.textPrimary, fontFamily: 'Playfair Display, serif' }}>
              Add to Wishlist
            </h3>
            <p className="text-sm mt-1 transition-colors" style={{ color: darkMode.textSecondary }}>
              Choose a wishlist for this gift
            </p>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            {loading ? (
              <div className="text-center py-8" style={{ color: darkMode.textSecondary }}>
                Loading wishlists...
              </div>
            ) : wishlists.length === 0 ? (
              <div className="text-center py-8">
                <Heart className="w-12 h-12 mx-auto mb-3" style={{ color: '#9E6752', opacity: 0.5 }} />
                <p className="text-base mb-2" style={{ color: darkMode.textSecondary }}>No wishlists yet</p>
                <p className="text-sm mb-4" style={{ color: darkMode.textSecondary }}>Create your first wishlist to start saving gifts</p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  style={{ background: '#9E6752', color: 'white' }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Wishlist
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {wishlists.map(wishlist => (
                  <button
                    key={wishlist.id}
                    onClick={() => handleAddToWishlist(wishlist.id)}
                    className="w-full p-4 rounded-xl transition-all hover:scale-[1.02]"
                    style={{
                      background: darkMode.isDark ? 'rgba(254, 215, 165, 0.1)' : '#F9FAFB',
                      border: `2px solid ${wishlist.color}`,
                      textAlign: 'left'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ background: wishlist.color }}
                      >
                        <Heart className="w-5 h-5 text-white" fill="white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold" style={{ color: darkMode.textPrimary }}>
                          {wishlist.name}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {wishlists.length > 0 && (
            <div className="px-6 py-4 border-t transition-colors flex-shrink-0" style={{ 
              borderColor: darkMode.isDark ? '#FED7A5' : '#E5E7EB', 
              background: darkMode.isDark ? 'rgba(254, 215, 165, 0.05)' : '#F9FAFB' 
            }}>
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-full flex items-center justify-center gap-2 py-2 text-sm hover:underline transition-colors"
                style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}
              >
                <Plus className="w-4 h-4" />
                Create New Wishlist
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Create Wishlist Modal */}
      {showCreateModal && (
        <CreateWishlistModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={async (newWishlist) => {
            setShowCreateModal(false);
            // Auto-add the current product/deck to the freshly-created wishlist
            // so the user doesn't have to pick it manually from the list.
            if (newWishlist?.id && (productId || (productIds && productIds.length))) {
              await handleAddToWishlist(newWishlist.id);
            } else {
              fetchWishlists();
            }
          }}
        />
      )}
    </>,
    document.body
  );
}

// Inline CreateWishlistModal component
function CreateWishlistModal({ isOpen, onClose, onSuccess }) {
  const darkMode = useDarkMode();
  const [name, setName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#FED7A5');
  const [submitting, setSubmitting] = useState(false);

  const colorOptions = [
    { name: 'Gold', value: '#FED7A5' },
    { name: 'Pink', value: '#E8B4B8' },
    { name: 'Blue', value: '#A4C8E1' },
    { name: 'Green', value: '#A8D5BA' },
    { name: 'Purple', value: '#D4B5E8' },
    { name: 'Red', value: '#E8A5A5' },
    { name: 'Orange', value: '#F5C094' },
    { name: 'Teal', value: '#9DD6D9' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a wishlist name');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/wishlists', {
        name: name.trim(),
        color: selectedColor
      });
      toast.success('Wishlist created!');
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (error) {
      logger.error('Error creating wishlist:', error);
      toast.error('Failed to create wishlist');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-md rounded-2xl shadow-2xl p-6"
        style={{ background: darkMode.isDark ? '#2D4354' : 'white' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}>
          Create Wishlist
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-2" style={{ color: darkMode.textPrimary }}>
              Wishlist Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Holiday 2025, Birthday Ideas"
              className="w-full px-4 py-2 rounded-lg border"
              style={{
                background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                color: darkMode.textPrimary,
                borderColor: darkMode.isDark ? '#FED7A5' : '#D1D5DB'
              }}
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium block mb-2" style={{ color: darkMode.textPrimary }}>
              Choose Color
            </label>
            <div className="grid grid-cols-4 gap-3">
              {colorOptions.map(color => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setSelectedColor(color.value)}
                  className="aspect-square rounded-lg transition-all hover:scale-110"
                  style={{
                    background: color.value,
                    border: selectedColor === color.value ? '3px solid #20212B' : '2px solid transparent',
                    boxShadow: selectedColor === color.value ? '0 0 0 2px white' : 'none'
                  }}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1"
              style={{
                background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : '#F3F4F6',
                color: darkMode.textPrimary
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1"
              style={{ background: '#9E6752', color: 'white' }}
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
